import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import apiGraph from "@/helpers/api/apiGraph";
import graphHelper from "@/helpers/utilities/GraphHelper";

import { Assignment, AssignmentResource, type Submission } from "@/models/Education/index";
import { CategoryExtension } from "@/models/extensions/education/CategoryExtension";

interface BatchResponseEntry {
  id: string;
  status: number;
  body?: { value?: unknown[] } & Record<string, unknown>;
}

interface GradeType {
  id: string;
  grades: Array<{ value: number; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface AssignmentResourceLike {
  id?: string;
  isDirty?: boolean;
  delete?: boolean;
  requestPayload: unknown;
  [key: string]: unknown;
}

export interface SubmissionLoaderApi {
  loadSubmissions: (assignmentList: Assignment[]) => Promise<void>;
  submissionsForAssignmentMap: Ref<Record<string, Submission[]>>;
}

export const useAssignmentsComposable = (submissionsApi: SubmissionLoaderApi) => {
  const assignments: Ref<Record<string, Assignment>> = ref({});
  const categoriesForAssignments: Ref<Record<string, unknown[]>> = ref({});
  const assignmentsForSectionMap: Ref<Record<string, string[]>> = ref({});
  const gradeTypes: Ref<GradeType[]> = ref([]);
  const assignmentGradeTypeMap: Ref<Record<string, unknown>> = ref({});
  const filterAndSortStatusForAssignments: Ref<unknown> = ref(null);

  const loadFromSkobo = async (assignmentId: string) => {
    const response = await apiSkobo.get(`/assignment/${assignmentId}`);
    return response.data ? response.data.assignment : null;
  };

  const saveToSkobo = async (assignment: Assignment) => {
    const assignmentObj = {
      gradeType: assignment.gradeType,
      title: assignment.displayName
    };
    await apiSkobo.post(`/assignment/${assignment.sectionId}/${assignment.id}`, assignmentObj);
  };

  const _persistAssignmentForSection = (sectionId: string, assignment: Assignment) => {
    assignments.value[assignment.id] = assignment;
    if (!assignmentsForSectionMap.value[sectionId]) {
      assignmentsForSectionMap.value[sectionId] = [];
    }
    if (!assignmentsForSectionMap.value[sectionId].includes(assignment.id)) {
      assignmentsForSectionMap.value[sectionId].push(assignment.id);
    }
  };

  const reloadForSection = async ({
    extSysApiSectionId,
    sectionId
  }: {
    extSysApiSectionId: string;
    sectionId: string;
  }) => {
    try {
      const response = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments`);
      const assignmentsList: Assignment[] = response.data.value.map((a: unknown) =>
        Assignment.parseFromGraph(a, sectionId, extSysApiSectionId)
      );
      const promises: Promise<unknown>[] = [];

      promises.push(submissionsApi.loadSubmissions(assignmentsList));

      promises.push(
        ...assignmentsList.map(async assignment => {
          const result = await loadFromSkobo(assignment.id);
          if (result) {
            assignment.gradeType = result.gradeType;
          }
        })
      );

      await Promise.all(promises);

      assignmentsForSectionMap.value[sectionId] = assignmentsList.map(a => a.id);
      assignmentsList.forEach(a => {
        assignments.value[a.id] = a;
      });
    } catch {
      assignmentsForSectionMap.value[sectionId] = [];
    }
  };

  const loadForSection = async ({
    extSysApiSectionId,
    sectionId
  }: {
    extSysApiSectionId: string;
    sectionId: string;
  }) => {
    if (!assignmentsForSectionMap.value[sectionId]) {
      await reloadForSection({ extSysApiSectionId, sectionId });
    }
  };

  const reloadAssignment = async (extSysApiSectionId: string, sectionId: string, assignmentId: string) => {
    const response = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}`);
    assignments.value[response.data.id] = Assignment.parseFromGraph(response.data, sectionId, extSysApiSectionId);
    return assignments.value[response.data.id];
  };

  const load = async (extSysApiSectionId: string, sectionId: string, assignmentId: string) => {
    if (!assignments.value[assignmentId]) {
      await reloadAssignment(extSysApiSectionId, sectionId, assignmentId);
    }
    return assignments.value[assignmentId];
  };

  const loadCategoriesForAssignments = (assignmentList: Assignment[]): Promise<void> => {
    return new Promise((resolve, reject) => {
      const arr: unknown[] = [];
      for (let i = 0; i < assignmentList.length; i++) {
        arr.push(CategoryExtension.generateGraphRequestFromAssignment(assignmentList[i]));
      }

      graphHelper.makeBatchRequest(arr).then(
        (result: BatchResponseEntry[]) => {
          for (let i = 0; i < result.length; i++) {
            if (result[i].status === 200) {
              categoriesForAssignments.value[result[i].id] = result[i].body?.value || [];
            }
          }
          resolve();
        },
        (error: unknown) => reject(error)
      );
    });
  };

  const loadResources = async ({
    extSysApiSectionId,
    assignmentId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
  }) => {
    const result = await apiGraph.get(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/resources`);
    const resources = result.data.value.map((r: unknown) => AssignmentResource.parseFromGraph(r));

    if (result && assignments.value[assignmentId]) {
      assignments.value[assignmentId].resources = resources;
    }
  };

  const updateAssignmentResources = async (assignment: Assignment) => {
    if (assignment.resources) {
      const promises = assignment.resources.map(async (res: AssignmentResourceLike) => {
        if (res.isDirty) {
          if (res.delete) {
            await apiGraph.delete(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources/${res.id}`
            );
            res = null as unknown as AssignmentResourceLike;
          } else if (!res.id) {
            const createResourceResponse = await apiGraph.post(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources`,
              res.requestPayload
            );
            return AssignmentResource.parseFromGraph(createResourceResponse.data);
          } else {
            await apiGraph.delete(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources/${res.id}`
            );
            const updateResourceResponse = await apiGraph.post(
              `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}/resources`,
              res.requestPayload
            );
            return AssignmentResource.parseFromGraph(updateResourceResponse.data);
          }
        } else {
          return res;
        }
      });

      const resultingResources = await Promise.all(promises);
      assignment.resources = resultingResources.filter(b => !!b);
    }
  };

  const updateAssignmentMetas = (assignment: Assignment) => {
    const promises: Promise<unknown>[] = [];
    promises.push(updateAssignmentResources(assignment));
    return Promise.all(promises);
  };

  const createAssignmentForSection = async (assignment: Assignment) => {
    const payload = assignment.createPayload;

    if (assignment.gradeType) {
      const gradeType = gradeTypes.value.find(gt => gt.id === assignment.gradeType);
      const largestValue = Math.max(...gradeType!.grades.map(grade => grade.value));
      payload.grading.maxPoints = largestValue;
    } else {
      payload.grading.maxPoints = 0;
    }

    const response = await apiGraph.post(`/education/classes/${assignment.extSysApiSectionId}/assignments`, payload);

    assignment.id = response.data.id;
    await saveToSkobo(assignment);

    const nctAssignment = Assignment.parseFromGraph(response.data, assignment.sectionId, assignment.extSysApiSectionId);

    _persistAssignmentForSection(assignment.sectionId, nctAssignment);
    return nctAssignment;
  };

  const updateAssignmentForSection = async (assignment: Assignment) => {
    const payload = assignment.patchPayload;

    if (assignment.gradeType) {
      const gradeType = gradeTypes.value.find(gt => gt.id === assignment.gradeType);
      const largestValue = Math.max(...gradeType!.grades.map(grade => grade.value));
      payload.grading.maxPoints = largestValue;
    } else {
      payload.grading.maxPoints = 0;
    }

    const response = await apiGraph.patch(
      `/education/classes/${assignment.extSysApiSectionId}/assignments/${assignment.id}`,
      payload
    );

    await saveToSkobo(assignment);

    const nctAssignment = Assignment.parseFromGraph(response.data, assignment.sectionId, assignment.extSysApiSectionId);

    _persistAssignmentForSection(assignment.sectionId, nctAssignment);
    return nctAssignment;
  };

  const save = async (assignment: Assignment) => {
    if (assignment.id) {
      const patchedAssignment = await updateAssignmentForSection(assignment);
      await updateAssignmentMetas(assignment);
      return patchedAssignment;
    } else {
      const savedAssignment = await createAssignmentForSection(assignment);
      assignment.id = savedAssignment.id;
      await updateAssignmentMetas(assignment);
      return savedAssignment;
    }
  };

  const deleteAssignment = (assignmentId: string): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      const assignment = assignments.value[assignmentId];

      apiGraph.delete(`/education/classes/${assignment.extSysApiSectionId}/assignments/${assignmentId}`).then(
        (response: unknown) => {
          delete assignments.value[assignmentId];
          resolve(response);
        },
        (error: unknown) => reject(error)
      );
    });
  };

  const publish = async ({
    extSysApiSectionId,
    assignmentId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
  }) => {
    await apiGraph.post(`/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/publish`);
  };

  const createResourcesFolder = async ({
    extSysApiSectionId,
    assignmentId
  }: {
    extSysApiSectionId: string;
    assignmentId: string;
  }): Promise<string | undefined> => {
    const noOfRetry = 5;
    let levelOfRetry = 0;

    if (!assignmentId || !extSysApiSectionId) {
      throw new Error("Input not valid");
    }

    const existing = assignments.value[assignmentId];
    if (existing?.resourcesFolderUrl && existing.resourcesFolderUrl !== "") {
      return existing.resourcesFolderUrl;
    }

    return await new Promise((resolve, reject) => {
      const intervaller = setInterval(async () => {
        try {
          const result = await apiGraph.post(
            `/education/classes/${extSysApiSectionId}/assignments/${assignmentId}/setUpResourcesFolder`
          );
          if (result && result.status === 200 && result.data && result.data.resourcesFolderUrl) {
            resolve(result.data.resourcesFolderUrl);
            clearInterval(intervaller);
          }
          if (
            result &&
            result.status === 400 &&
            result.innerError &&
            result.innerError.code === "folderAlreadyExists"
          ) {
            resolve(undefined);
          }
        } finally {
          if (levelOfRetry > noOfRetry) {
            reject();
            clearInterval(intervaller);
          }
          levelOfRetry++;
        }
      }, 2000);
    });
  };

  const loadGradeTypes = async () => {
    if (gradeTypes.value && gradeTypes.value.length > 0) {
      return;
    }
    const response = await apiSkobo.get("/assignment/setup");
    gradeTypes.value = response.data;
  };

  const setFilterAndSortStatusForAssignments = (status: unknown) => {
    filterAndSortStatusForAssignments.value = status;
  };

  const setGradetypeOnAssignment = ({ assignmentId, gradeType }: { assignmentId: string; gradeType: unknown }) => {
    assignmentGradeTypeMap.value[assignmentId] = gradeType;
  };

  // Getters
  const allAssignments = computed(() => assignments.value);
  const assignment = computed(() => (assignmentId: string) => assignments.value[assignmentId]);
  const assignmentsForSection = computed(() => (sectionId: string) => {
    if (assignmentsForSectionMap.value[sectionId]) {
      return assignmentsForSectionMap.value[sectionId].map(a => assignments.value[a]);
    }
    return [];
  });
  const submissionsForAssignment = computed(
    () => (assignmentId: string) => submissionsApi.submissionsForAssignmentMap.value[assignmentId] || []
  );
  const submissionForAssignment = computed(() => (assignmentId: string, submissionId: string) => {
    const submissions = submissionsApi.submissionsForAssignmentMap.value[assignmentId];
    if (submissions) {
      return submissions.find((s: Submission) => (s as unknown as { id: string }).id === submissionId);
    }
    return null;
  });
  const categoriesForAssignment = computed(
    () => (assignmentId: string) => categoriesForAssignments.value[assignmentId] || []
  );
  const gradeTypeForAssignment = computed(
    () => (assignmentId: string) => assignmentGradeTypeMap.value[assignmentId] || []
  );

  return {
    // state
    assignments,
    categoriesForAssignments,
    assignmentsForSectionMap,
    gradeTypes,
    assignmentGradeTypeMap,
    filterAndSortStatusForAssignments,
    // actions
    loadForSection,
    reloadForSection,
    load,
    reloadAssignment,
    loadCategoriesForAssignments,
    loadResources,
    save,
    createAssignmentForSection,
    updateAssignmentForSection,
    _persistAssignmentForSection,
    updateAssignmentMetas,
    updateAssignmentResources,
    deleteAssignment,
    publish,
    createResourcesFolder,
    loadGradeTypes,
    saveToSkobo,
    loadFromSkobo,
    setFilterAndSortStatusForAssignments,
    setGradetypeOnAssignment,
    // getters
    allAssignments,
    assignment,
    assignmentsForSection,
    submissionsForAssignment,
    submissionForAssignment,
    categoriesForAssignment,
    gradeTypeForAssignment
  };
};
